/**
 * Figma: https://www.figma.com/design/XwMQV9iBJ1Ec4sOzWMEZcK/-Dev--Customer-Hub-2.3-%E2%80%93-Phase-1?node-id=2226-106325&m=dev
 *
 * Order state card: shipment status, its garments, and the order timeline.
 */

import { Icon } from '@susu/ui/components'
import { useState } from 'react'

import { useLabel } from '../../../i18n'
import { buildCdnImageUrl } from '../../../utils/imageUrl'
import OrderStatusTimeline from '../shared/OrderStatusTimeline'

import type { IconName } from '@susu/ui/components'
import type {
  OrderStateCard as OrderStateCardData,
  OrderStateCta,
  OrderStateCtaKind,
  OrderStateProduct,
  OrderStateRowType,
  TimelineLabels,
} from '../types'

interface OrderStateCardProps {
  card: OrderStateCardData
  /** Translated timeline captions, supplied by the host. */
  timelineLabels?: TimelineLabels
  onCtaClick?: (card: OrderStateCardData, cta: OrderStateCta) => void
}

/**
 * Leading icon, whether the action leaves the hub (trailing icon), and whether
 * it opens in a new window instead of navigating the hub away.
 */
const CTA_PRESENTATION: Record<
  OrderStateCtaKind,
  { icon: IconName; isExternal: boolean; newWindow: boolean }
> = {
  trackOrder: { icon: 'delivery', isExternal: true, newWindow: true },
  returnOrExchange: {
    icon: 'return-arrow',
    isExternal: false,
    newWindow: false,
  },
  // Reached through the hub rather than beside it: the booking tool replaces
  // the page like any other in-hub destination.
  bookPickupAppointment: {
    icon: 'store-appointment',
    isExternal: false,
    newWindow: false,
  },
  // The label lives with the carrier, so it opens away from the hub.
  showQrCode: { icon: 'qr', isExternal: false, newWindow: true },
}

/**
 * Thumbnail badge per row type. A refund is one-way, so it gets the U-turn
 * arrow; an exchange swaps one garment for another, so it gets the two opposed
 * arrows the Figma card uses. A purchase carries no badge.
 */
const ROW_TYPE_BADGE: Record<OrderStateRowType, IconName | null> = {
  order: null,
  return: 'return-arrow',
  exchange: 'change',
}

export default function OrderStateCard({
  card,
  timelineLabels,
  onCtaClick,
}: OrderStateCardProps) {
  return (
    <div
      className="flex flex-col rounded-lg border border-border bg-surface"
      data-testid="order-state-card"
    >
      <div className="flex w-full flex-col gap-16 px-16 pt-14 pb-16">
        <div className="flex w-full flex-col gap-2 text-text [word-break:break-word]">
          <span
            className="text-base leading-base font-medium"
            data-testid="order-state-status"
          >
            {card.status}
          </span>
          {card.subtitle && (
            <span
              className="text-sm leading-sm font-light"
              data-testid="order-state-delivery"
            >
              {card.subtitle}
            </span>
          )}
        </div>

        {card.products.length > 0 && (
          <div className="flex w-full items-start gap-8 overflow-x-auto">
            {card.products.map((product, index) => (
              <ProductThumbnail
                key={`${product.imageUrl}-${index}`}
                product={product}
                rowType={card.rowType}
              />
            ))}
          </div>
        )}

        {card.showTimeline && (
          <div className="w-full pt-4">
            <OrderStatusTimeline
              currentStep={card.currentStep}
              variant={card.timelineVariant}
              labels={timelineLabels}
            />
          </div>
        )}
      </div>

      {card.cta && (
        <CtaRow cta={card.cta} onClick={(cta) => onCtaClick?.(card, cta)} />
      )}
    </div>
  )
}

/**
 * iOS Safari turns a `download` anchor into a download-only prompt. Without the
 * attribute it opens the label in a new tab, and its modal offers View as well.
 * iPadOS reports itself as a Mac, hence the touch-points check.
 */
const isIos = () =>
  typeof navigator !== 'undefined' &&
  (/iP(hone|ad|od)/.test(navigator.userAgent) ||
    (navigator.userAgent.includes('Macintosh') && navigator.maxTouchPoints > 1))

const CTA_ROW_CLASS =
  'flex w-full items-center justify-between rounded-br-lg rounded-bl-lg border-t border-border px-16 pt-16 pb-20 text-left transition-[opacity] hover:opacity-80'

function CtaRow({
  cta,
  onClick,
}: {
  cta: OrderStateCta
  onClick: (cta: OrderStateCta) => void
}) {
  const { icon, isExternal, newWindow } = CTA_PRESENTATION[cta.kind]

  const sharedProps = {
    className: CTA_ROW_CLASS,
    'data-testid': 'order-state-cta',
    'data-cta-kind': cta.kind,
    // Read by tests and QA on both elements, so the destination is in one
    // place whether or not the row carries a real href.
    'data-cta-href': cta.href,
  }

  const content = (
    <>
      <span className="flex items-center gap-16">
        <Icon name={icon} size="medium" />
        <span className="flex items-center gap-4">
          <span className="flex items-center justify-center pb-2 text-sm leading-sm font-medium text-text">
            {cta.label}
          </span>
        </span>
      </span>
      <Icon
        name={isExternal ? 'arrow-top-right' : 'chevron-right'}
        size="medium"
      />
    </>
  )

  /**
   * A new-tab CTA has to be a real anchor: the hub is embedded in Predict
   * Spring, which blocks `window.open` — opening in script there does nothing
   * at all. `isExternal` is unrelated; it only picks the trailing icon.
   */
  if (newWindow) {
    return (
      <a
        {...sharedProps}
        href={cta.href}
        download={isIos() ? undefined : cta.downloadName}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => onClick(cta)}
      >
        {content}
      </a>
    )
  }

  /**
   * Same-window navigation stays scripted, and reports to the host first so
   * its analytics call is dispatched before the page starts tearing down.
   */
  const handleClick = () => {
    onClick(cta)
    window.location.href = cta.href
  }

  return (
    <button type="button" {...sharedProps} onClick={handleClick}>
      {content}
    </button>
  )
}

function ProductThumbnail({
  product,
  rowType = 'order',
}: {
  product: OrderStateProduct
  rowType?: OrderStateRowType
}) {
  const t = useLabel()
  const [imageError, setImageError] = useState(false)
  const badgeIcon = ROW_TYPE_BADGE[rowType]

  return (
    <div className="relative flex h-90 w-75 shrink-0 flex-col items-center justify-center">
      <div
        className="relative flex h-90 w-75 flex-col items-center justify-center overflow-hidden rounded-md border border-border bg-background-gray-dark p-8"
        data-testid="order-state-product"
      >
        {!imageError && product.imageUrl ? (
          <img
            src={buildCdnImageUrl(product.imageUrl)}
            alt=""
            loading="lazy"
            decoding="async"
            onError={() => setImageError(true)}
            className="h-full w-full shrink-0 object-contain"
          />
        ) : (
          <svg
            width="40"
            height="40"
            viewBox="0 0 40 40"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <title>{t('orderList.item.noImage')}</title>
            <rect width="40" height="40" rx="4" fill="#E5E5E5" />
            <path d="M12 28L17 23L21 27L26 22L28 24V28H12Z" fill="#C6C6C6" />
            <circle cx="24" cy="16" r="3" fill="#C6C6C6" />
          </svg>
        )}
      </div>

      {product.isCustomMade && (
        <div className="backdrop-blur-sm absolute top-6 left-6 flex h-22 w-22 items-center justify-center rounded-md bg-white-alpha-600">
          <Icon name="custom-made" size={14} />
        </div>
      )}

      {/* Return/exchange badge — bare glyph in the opposite corner from the
          Custom Made chip, so a Custom Made return shows both. */}
      {badgeIcon && (
        <div
          className="absolute top-5 right-5 flex items-center justify-center"
          data-testid="order-state-product-badge"
          data-badge={badgeIcon}
        >
          <Icon name={badgeIcon} size={12} color="secondary" />
        </div>
      )}
    </div>
  )
}
